import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Bell, Brain } from "lucide-react";

const PriceComparison = () => {
  // Keep the pagerless bar simple: show only the AI portion ($8 / 10k alerts).
  const pagerlessAiValue = 8;

  const competitors = [
    { kind: "competitor", name: "VictorOps", price: 29, priceLabel: "$29/user", estimated: false },
    { kind: "competitor", name: "xMatters", price: 25, priceLabel: "$25/user", estimated: false },
    { kind: "competitor", name: "PagerDuty", price: 21, priceLabel: "$21/user", estimated: false },
    { kind: "competitor", name: "Zenduty", price: 24, priceLabel: "$24/user", estimated: true },
    { kind: "competitor", name: "Grafana OnCall (IM)", price: 18, priceLabel: "$18/user", estimated: true },
    {
      kind: "pagerless",
      name: "Pagerless",
      price: pagerlessAiValue,
      priceLabel: "$8",
      subLabel: "/10k alerts",
      segments: [{ label: "AI Analysis", value: pagerlessAiValue, colorClass: "bg-blue-500/30" }]
    }
  ];

  const sortedCompetitors = [...competitors].sort((a, b) => b.price - a.price);
  const maxPrice = Math.max(...sortedCompetitors.map((c) => c.price));

  return (
    <section className="py-24 md:py-32 bg-[#0A0A0A] overflow-hidden">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">The math is simple</h2>
          <p className="text-lg text-gray-400">They charge per user. We charge for what you use.</p>
        </motion.div>

        {/* Competitor bars */}
        <div className="space-y-4 mb-12">
          {sortedCompetitors.map((comp, i) => (
            <motion.div
              key={comp.name}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="relative"
            >
              <div className="flex items-center gap-4">
                <div className="w-36 text-right">
                  <span className="text-sm font-medium text-gray-400">{comp.name}</span>
                </div>
                <div className="flex-1 relative h-12">
                  <div className="absolute inset-0 rounded-lg bg-white/5" />
                  {comp.kind === "pagerless" ? (
                    <>
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${(comp.price / maxPrice) * 100}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + i * 0.1, duration: 0.8, ease: "easeOut" }}
                        className="absolute inset-y-0 left-0 rounded-lg bg-blue-500/30"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <div className="flex flex-col items-end leading-tight">
                          <span className="text-lg font-bold text-blue-300">{comp.priceLabel}</span>
                          {comp.subLabel && (
                            <span className="text-[11px] text-gray-500 font-medium">{comp.subLabel}</span>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(comp.price / maxPrice) * 100}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1, duration: 0.8, ease: "easeOut" }}
                      className="absolute inset-y-0 left-0 rounded-lg flex items-center justify-end pr-4 bg-white/10"
                    >
                      <div className="flex flex-col items-end leading-tight">
                        <span className="text-lg font-bold text-gray-300">{comp.priceLabel}</span>
                        {comp.estimated && (
                          <span className="text-[11px] text-gray-500 font-medium">*estimated</span>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pagerless Pricing */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="grid md:grid-cols-2 gap-6"
        >
          <div className="p-6 rounded-2xl bg-emerald-500/10 border border-emerald-500/30">
            <div className="flex items-center gap-3 mb-3">
              <Bell className="w-6 h-6 text-emerald-400" />
              <span className="text-emerald-400 font-medium">Basic Paging</span>
            </div>
            <div className="text-4xl font-bold text-white mb-2">
              $1<span className="text-lg text-gray-400">/user/mo</span>
            </div>
            <p className="text-gray-400 text-sm">Reliable alerting, on-call schedules, escalations, all integrations</p>
          </div>
          <div className="p-6 rounded-2xl bg-blue-500/10 border border-blue-500/30 relative overflow-hidden">
            <div className="absolute top-3 right-3">
              <span className="px-2 py-1 rounded-full bg-blue-500 text-white text-xs font-bold">PAY AS YOU GO</span>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <Brain className="w-6 h-6 text-blue-400" />
              <span className="text-blue-400 font-medium">AI Analysis</span>
            </div>
            <div className="text-4xl font-bold text-white mb-2">
              $8<span className="text-lg text-gray-400">/10k alerts</span>
            </div>
            <p className="text-gray-400 text-sm">AI root cause analysis, auto-fix suggestions, 87% noise reduction</p>
          </div>
        </motion.div>

        {/* Example calculation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7 }}
          className="mt-12 text-center"
        >
          <div className="inline-block px-8 py-6 rounded-2xl bg-[#050505] border border-white/10">
            <div className="text-sm text-gray-500 mb-2">Example: 50 users, 20k alerts/month</div>
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="text-gray-500 text-xs">PagerDuty</div>
                <div className="text-2xl font-bold text-red-400 line-through">$1,050/mo</div>
              </div>
              <div className="text-gray-600">vs</div>
              <div className="text-center">
                <div className="text-gray-500 text-xs">Pagerless</div>
                <div className="text-2xl font-bold text-emerald-400">$66/mo</div>
                <div className="text-xs text-gray-500">($50 + $16 AI)</div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 btn-primary px-8 py-4 rounded-lg text-white font-medium text-lg"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export { PriceComparison };

